import { twMerge } from 'tailwind-merge'

import { wrapUrlsInLinks } from '@unreserved-frontend-v2/utils/links'

import { InfoIcon } from '../icons/info'

export type BaseLabelProps = {
  /** Unique id of the form control, from the back-end, used to associate label with input */
  id: string

  /** Text label to be rendered */
  label: string

  /** Whether or not we render in mobile mode */
  isMobile?: boolean

  /** Whether or not we have encountered errors for the input this label belongs to */
  isErrored?: boolean

  /** Description of the input if applicable */
  description?: string | null

  /** Whether or not the input is required */
  isRequired?: boolean

  /** Override classes on the required circle */
  circleClassName?: string
}

export const BaseLabel = ({
  id,
  label,
  isMobile,
  isErrored,
  description,
  isRequired,
  circleClassName,
}: BaseLabelProps) => {
  /** used by aria-labeledby for custom controls  */
  const labelId = `${id}-label`
  const circle = (
    <div
      className={twMerge(
        'h-[6px] w-[6px] shrink-0 rounded-full bg-shades-800',
        circleClassName,
        isErrored ? 'bg-red-800' : null
      )}
    />
  )

  if (isMobile) {
    return (
      <>
        <div
          className={twMerge(
            'flex items-center justify-between pb-1.5 leading-7',
            description ? 'pb-1 font-bold' : null
          )}
        >
          <label id={labelId} htmlFor={id}>
            {label}
          </label>{' '}
          {isRequired ? circle : null}{' '}
        </div>
        {description ? (
          <div className="w-full flex-wrap pb-2.5 font-normal leading-7">{wrapUrlsInLinks(description)}</div>
        ) : null}
      </>
    )
  }

  const tooltipDescriptionId = `${id}Description`

  const tooltip = (
    <div className="group relative ml-3">
      <InfoIcon className="text-shades-600" />
      <div className="absolute bottom-4 right-[-175px] hidden w-[350px] pb-2 group-hover:inline-block">
        <div
          id={tooltipDescriptionId}
          role="tooltip"
          className="rounded border border-shades-200 bg-white px-4 py-3 text-sm leading-5 text-shades"
        >
          {wrapUrlsInLinks(description || '')}
        </div>
      </div>
    </div>
  )

  return (
    <div className={twMerge(`flex items-center pb-1.5 leading-7`, !description ? 'justify-between' : '')}>
      <div className="flex flex-grow items-center">
        <label id={labelId} aria-describedby={tooltipDescriptionId} htmlFor={id}>
          {label}
        </label>
        {description ? tooltip : null}
      </div>
      {isRequired ? circle : null}
    </div>
  )
}
